import { sharedAppStateStore } from 'src/store/AppStateStore'
import { sharedDataStore } from 'src/store/DataStore'
import { User } from 'src/models/User'
import { Paths } from 'src/constants/navigation'
import { showNotification } from 'src/hoc/Notification'
import moment from 'moment'
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs'

export type GoogleDriveCallbackDoc = CallbackDoc

export interface OneDriveCallbackDoc {
  name: string
  webDavUrl: string
  webUrl: string
  size: number
  id: string
  parentReference: ParentReference
  sharepointIds: OneDriveCallbackDocSharepointIds
  '@sharePoint.embedUrl': string
  '@sharePoint.endpoint': string
  '@sharePoint.listUrl': string
}

export interface ParentReference {
  driveId: string
  sharepointIds: ParentReferenceSharepointIds
}

export interface ParentReferenceSharepointIds {
  listId: string
  webId: string
  siteId: string
  siteUrl: string
}

export interface OneDriveCallbackDocSharepointIds {
  listItemUniqueId: string
  listItemId: string
  listId: string
  webId: string
  siteId: string
  siteUrl: string
}

export class OAuth {
  static initMicrosoftOAuthFlow = (callback: (token: string, baseUrl: string) => void, popupWindow?: Window) => {
    try {
      const params = new URLSearchParams({
        client_id: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
        scope: process.env.REACT_APP_MICROSOFT_SCOPES,
        response_type: 'code',
        redirect_uri: Paths.getMicrosoftOAuthRedirectURL(),
      })

      const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`
      const width = 600
      const height = 700
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2
      const popup =
        popupWindow ||
        window.open(authUrl, 'microsoft-login', `width=${width},height=${height},left=${left},top=${top}`)

      // Set URL for both cases
      popup.location.href = authUrl

      const handleMessage = async (event) => {
        if (event.origin !== window.location.origin) return
        if (event.data.type === 'microsoft_auth_code') {
          const code = event.data.code
          try {
            const { data } = await User.connectMicrosoft({ code })
            sharedDataStore.user = User.fromData(data)
            callback(
              sharedDataStore.user.microsoftDriveAccessToken.token,
              sharedDataStore.user.microsoftDriveAccessToken.sharepointBaseUrl
            )
          } catch (error) {
            showNotification({ message: 'Microsoft connection failed. Please contact support', type: 'error' })
            sharedAppStateStore.handleError(error)
          } finally {
            if (!popupWindow) popup?.close()
            window.removeEventListener('message', handleMessage)
          }
        } else if (event.data.type === 'microsoft_auth_error') {
          showNotification({ message: 'Microsoft connection failed. Please contact support', type: 'error' })
          if (!popupWindow) popup?.close()
          window.removeEventListener('message', handleMessage)
        }
      }
      window.addEventListener('message', handleMessage)
    } catch (error) {
      showNotification({ message: 'Microsoft connection failed. Please contact support', type: 'error' })
      popupWindow?.close()
      sharedAppStateStore.handleError(error)
    }
  }

  static getMicrosoftAccessToken = async (callback: (token: string, baseUrl: string) => void, popupWindow?: Window) => {
    const { microsoftDriveAccessToken: existingToken } = sharedDataStore.user
    if (existingToken && existingToken.expiry.isAfter(moment.utc())) {
      callback(existingToken.token, existingToken.sharepointBaseUrl)
    } else if (existingToken) {
      try {
        const { data } = await sharedAppStateStore.wrapAppLoading(
          User.refreshDriveAccessToken('microsoft'),
          'Loading microsoft...'
        )
        sharedDataStore.user = User.fromData(data)
        callback(
          sharedDataStore.user.microsoftDriveAccessToken.token,
          sharedDataStore.user.microsoftDriveAccessToken.sharepointBaseUrl
        )
      } catch (err) {
        // if refresh fails for any reason attempt to re-init oauth flow
        OAuth.initMicrosoftOAuthFlow(callback, popupWindow)
      }
    } else {
      OAuth.initMicrosoftOAuthFlow(callback, popupWindow)
    }
  }

  static initGoogleDriveOAuthFlow = (callback: (token: string) => void) => {
    const scopes = process.env.REACT_APP_GDRIVE_SCOPES.split(',')
      .map((path) => `https://www.googleapis.com/${path}`)
      .join(' ')

    // @ts-ignore -- react-google-drive-picker creates a global variable
    const client = google.accounts.oauth2.initCodeClient({
      client_id: process.env.REACT_APP_GDRIVE_CLIENT_ID,
      ux_mode: 'popup',
      scope: scopes,
      // Because we use `popup` there is no redirect uri, so have to pass `postmessage` instead.
      // Ref: https://stackoverflow.com/questions/55222501/google-oauth-redirect-uri-mismatch-when-exchanging-one-time-code-for-refresh-tok?rq=1
      redirect_uri: 'postmessage',
      callback: async (res: any) => {
        try {
          const { data } = await sharedAppStateStore.wrapAppLoading(
            User.connectGoogle({ code: res.code }),
            'Loading google drive...'
          )
          sharedDataStore.user = User.fromData(data)
          callback(sharedDataStore.user.googleDriveAccessToken.token)
        } catch (err) {
          sharedAppStateStore.handleError(err)
        }
      },
    })

    client.requestCode()
  }

  static getGoogleDriveAccessToken = async (callback: (token: string) => void) => {
    const { googleDriveAccessToken: existingToken } = sharedDataStore.user
    if (existingToken && existingToken.expiry.isAfter(moment.utc())) {
      callback(existingToken.token)
    } else if (existingToken) {
      try {
        const { data } = await sharedAppStateStore.wrapAppLoading(
          User.refreshDriveAccessToken('google'),
          'Loading google drive...'
        )
        sharedDataStore.user = User.fromData(data)
        callback(sharedDataStore.user.googleDriveAccessToken.token)
      } catch (err) {
        // if refresh fails for any reason attempt to re-init oauth flow
        OAuth.initGoogleDriveOAuthFlow(callback)
      }
    } else {
      OAuth.initGoogleDriveOAuthFlow(callback)
    }
  }
}
